import React, { useState } from 'react';
import {
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    fetchSignInMethodsForEmail,
    GoogleAuthProvider,
} from 'firebase/auth';
import {
    Container,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Box,
    Divider,
    Alert,
    Link,
} from '@mui/material';
import firebaseAuth, {
    googleProvider,
    handlePostLogin,
} from 'Modules/firebase/firebase-auth';
import useAuth, { AuthLoginPrams } from 'Modules/firebase/use-auth';
import { useOperator } from 'app/contexts/OperatorContext';
import { appRoutes } from 'app/routes/constants';
import LoadingPage from './LoadingPage';
import Loader from 'Components/Loader';
import qualityIcon from '../../static/assets/icons/branding/quality_icon.svg';
import PasswordField from 'Components/PasswordField';
import { useOrgNavigate } from 'app/routes/useOrgNavigate';
import { useSearchParams } from 'react-router-dom';
import { getOperatorTenantUrl } from '@/operator/utils/env/urlStrings';
import { useSelector } from 'react-redux';
import { selectUserOrganizations } from 'store/user/userSelector';
import { useSubdomain } from 'hooks/useSubdomain';
import AppVersion from 'Components/Common/AppVersion';
import { Login, LoginSchema, SignUpSchema } from '@/schemas/login.schema';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

export type LoginActionType = 'logIn' | 'signUp' | 'invite';

interface LoginPageProps {
    params?: AuthLoginPrams;
    action?: LoginActionType;
}

const GoogleLogo: React.FC = () => (
    <svg
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        width={'24px'}
        height={'24px'}
        style={{ display: 'block' }}
    >
        <path
            fill="#EA4335"
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
            fill="#4285F4"
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
            fill="#FBBC05"
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
            fill="#34A853"
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
        <path fill="none" d="M0 0h48v48H0z" />
    </svg>
);

const LoginPage: React.FC<LoginPageProps> = ({ params, action = 'logIn' }) => {
    const { isSubdomainRoute, subdomain } = useSubdomain();
    const { organizationId, setOrganizationId } = useOperator();
    const {
        setError: setErrorAuth,
        loading: authLoading,
        isAuthenticated,
    } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useOrgNavigate();
    const userOrganizations = useSelector(selectUserOrganizations);
    const actionParam = searchParams.get('action') || 'logIn';

    const [error, setError] = useState<string | null>(null);
    const [isLogin, setIsLogin] = useState(actionParam === 'logIn');
    const [message, setMessage] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors: formErrors, isValid, isLoading, isSubmitting },
        control,
    } = useForm<Login>({
        resolver: valibotResolver(isLogin ? LoginSchema : SignUpSchema),
        defaultValues: {
            email: searchParams.get('email') || '',
            password: '',
        },
        mode: 'onChange',
        shouldUnregister: false,
    });

    const email = watch('email');
    const password = watch('password');

    const finishLogin = async () => {
        try {
            if (isSubdomainRoute) {
                const pickedOrg = userOrganizations?.find(
                    (org) => org.slug === subdomain,
                );
                setOrganizationId(pickedOrg?.orgId);
            }
            await handlePostLogin(
                firebaseAuth.currentUser,
                params,
                organizationId,
                action,
            );

            if (action === 'invite') {
                navigate(appRoutes.HOME);
                return;
            }

            if (params) {
                const { appState } = params;
                if (appState) {
                    const { targetUrl } = appState;
                    if (targetUrl) {
                        navigate(targetUrl);
                    }
                }
            }
        } catch (postLoginError) {
            setErrorAuth(postLoginError.message);
        }
    };

    const handleLoginError = async (error: any) => {
        try {
            let message = error.message;
            let errorCode = error.code;

            if (errorCode === 'auth/user-not-found') {
                message = 'Email not found, please try to sign up';
            } else if (errorCode === 'auth/email-already-in-use') {
                message = 'Email already in use.';
            } else if (errorCode === 'auth/wrong-password') {
                message = 'Wrong password. Try resetting it.';
            } else if (
                errorCode === 'auth/account-exists-with-different-credential'
            ) {
                const signInMethods = await fetchSignInMethodsForEmail(
                    firebaseAuth,
                    email,
                );
                if (signInMethods.length === 1) {
                    message = signInMethods.includes(
                        GoogleAuthProvider.GOOGLE_SIGN_IN_METHOD,
                    )
                        ? 'You previously signed up with Google. Please log in using Google.'
                        : 'You previously signed up with Password. Please log in using Email and Password.';
                }
            }
            setError(message);
        } catch (err) {
            setError(err.message);
        }
    };

    const initializeState = () => {
        setError(null);
        setMessage(null);
        setErrorAuth(null);
    };

    const handleGoogleLogin = async () => {
        initializeState();
        try {
            await signInWithPopup(firebaseAuth, googleProvider);
            await finishLogin();
        } catch (error: any) {
            handleLoginError(error);
        }
    };

    const handleFirebaseAuth: SubmitHandler<Login> = async () => {
        initializeState();

        try {
            if (isLogin) {
                await signInWithEmailAndPassword(firebaseAuth, email, password);
            } else {
                await createUserWithEmailAndPassword(
                    firebaseAuth,
                    email,
                    password,
                );
            }
            await finishLogin();
        } catch (error: any) {
            handleLoginError(error);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setError('Please enter your email to reset the password.');
            return;
        }

        setError('');

        const url = getOperatorTenantUrl();
        const actionCodeSettings = {
            url: `${url}/${appRoutes.RESET_PASSWORD}`,
            handleCodeInApp: true,
        };
        try {
            await sendPasswordResetEmail(
                firebaseAuth,
                email,
                actionCodeSettings,
            );
            setMessage('Password reset email sent! Check your inbox.');
        } catch (error: any) {
            handleLoginError(error);
        }
    };

    const renderHeader = (): React.ReactNode => {
        if (isLogin) return 'Welcome';

        return 'Create Your Account';
    };

    const renderTitle = (): React.ReactNode => {
        if (isLogin) return 'Login into Quality';

        let signUpTitle = 'Sign Up to Quality';
        if (action === 'invite') {
            signUpTitle = `Accept your invitation to ${signUpTitle}`;
        }
        return signUpTitle;
    };

    if (isLoading || authLoading || isAuthenticated || isSubmitting) {
        return (
            <LoadingPage container>
                <Loader message="Checking for credentials" />
            </LoadingPage>
        );
    }

    return (
        <Container maxWidth="xs">
            <Card sx={{ mt: 8, p: 3, boxShadow: 3 }}>
                <CardContent>
                    <Typography align="center" gutterBottom>
                        <img src={qualityIcon} height="45px" />
                    </Typography>
                    <Typography variant="h4" align="center" gutterBottom>
                        {renderHeader()}
                    </Typography>
                    <Typography variant="body2" align="center" gutterBottom>
                        {renderTitle()}
                    </Typography>

                    {error && <Alert severity="error">{error}</Alert>}
                    {message && <Alert severity="success">{message}</Alert>}

                    <Box
                        component="form"
                        onSubmit={handleSubmit(handleFirebaseAuth)}
                        sx={{ mt: 2 }}
                    >
                        <TextField
                            label="Email address"
                            type="email"
                            fullWidth
                            margin="normal"
                            error={!!formErrors?.email?.message}
                            helperText={formErrors?.email?.message}
                            required
                            {...register('email')}
                        />
                        <Controller
                            name="password"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <PasswordField
                                    label="Password"
                                    value={value}
                                    onChange={onChange}
                                    error={!!formErrors?.password?.message}
                                    helperText={formErrors?.password?.message}
                                />
                            )}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{ mt: 2 }}
                            disabled={!isValid}
                        >
                            {isLogin ? 'Login' : 'Sign Up'}
                        </Button>
                    </Box>

                    <Box textAlign="center" sx={{ mt: 2 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => setIsLogin(!isLogin)}
                        >
                            {isLogin
                                ? "Don't have an account? Sign up"
                                : 'Already have an account? Login'}
                        </Link>
                    </Box>

                    {isLogin && (
                        <Box textAlign="center" sx={{ mt: 1 }}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={handleForgotPassword}
                            >
                                Forgot Password?
                            </Link>
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }}>OR</Divider>

                    <Button
                        onClick={handleGoogleLogin}
                        variant="outlined"
                        fullWidth
                        startIcon={<GoogleLogo />}
                        color="inherit"
                        sx={{
                            padding: '10px 20px',
                            borderColor: (theme) =>
                                theme.palette.border?.main || '#e0e0e0',
                        }}
                    >
                        Login with Google
                    </Button>
                </CardContent>
                <AppVersion />
            </Card>
        </Container>
    );
};

export default LoginPage;
